import React, { useState, useRef, useEffect } from 'react';
import { chatbotCss } from '@digital_monorepo/styling';
import apiClient from '@/api/client';
import Icon from '@/components/common/Icon';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);

  const toggleChat = () => setIsOpen(prev => !prev);

  // Auto-scroll to newest message
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  const sendMessage = async () => {
    if (!userInput.trim() || loading) return;

    const newMessages = [...messages, { id: Date.now(), sender: 'user', text: userInput }];
    setMessages(newMessages);
    setUserInput('');
    setLoading(true);

    // Reset textarea height after sending
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    try {
      const response = await apiClient.post('/chatbot', { userInput });
      setMessages([...newMessages, { id: Date.now() + 1, sender: 'bot', text: response.data.message }]);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages([...newMessages, { id: Date.now() + 1, sender: 'bot', text: 'Error: Unable to connect to chatbot.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Auto-resize textarea
  const handleInput = e => {
    setUserInput(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  return (
    <div className={chatbotCss.chatbotContainer}>
      <button
        className={chatbotCss.chatButton}
        onClick={toggleChat}
        style={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1100
        }}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}>
        <Icon name={isOpen ? 'close' : 'chat'} size={19} />
      </button>
      {isOpen && (
        <div className={chatbotCss.chatbox}>
          <div className={chatbotCss.header}>
            <div className={chatbotCss.headerAvatar}>
              <Icon name='chat' size={18} />
            </div>
            <div>
              <div className={chatbotCss.headerTitle}>Scheduling Assistant</div>
              <div className={chatbotCss.headerSubtitle}>Ask about your team</div>
            </div>
          </div>

          <div className={chatbotCss.messages}>
            {messages.map(msg => (
              <div key={msg.id} className={`${chatbotCss.messageRow} ${msg.sender === 'user' ? chatbotCss.messageRowUser : chatbotCss.messageRowBot}`}>
                <div className={msg.sender === 'user' ? chatbotCss.userMessage : chatbotCss.botMessage}>
                  {msg.sender === 'bot' ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown> : msg.text}
                </div>
              </div>
            ))}

            {loading && (
              <div className={`${chatbotCss.messageRow} ${chatbotCss.messageRowBot}`}>
                <div className={chatbotCss.botMessage}>
                  <div className={chatbotCss.typing} aria-label='Loading...'>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className={chatbotCss.inputContainer}>
            <textarea ref={textareaRef} value={userInput} onChange={handleInput} onKeyDown={handleKeyDown} className={chatbotCss.input} placeholder='Type a message...' disabled={loading} rows={1} />
            <button onClick={sendMessage} className={chatbotCss.sendButton} disabled={loading || !userInput.trim()} aria-label='Send message'>
              ➤
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chatbot;
